import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import { apiPost } from '../utils/apiUtils';
import SaveFilterDialog from './dialogs/SaveFilterDialog';
import ListCleanerCreditEstimator from './usage/ListCleanerCreditEstimator';
import AutoCompleteField from './common/AutoCompleteField';
import PreviewModal from './PreviewModal';

/**
 * ConfigureFilters component - allows configuration of filter parameters and column-specific rules
 */
const ConfigureFilters = ({
  onNext,
  onBack,
  setLoading,
  fileData,
  selectedColumns,
  filterConfig,
  setFilterConfig,
  filterParams,
  setFilterParams,
  setFilterId,
  jobName,
  onSuccess
}) => {
  const [error, setError] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  // Credit estimation state
  const [creditCheck, setCreditCheck] = useState(null);
  const [canSubmit, setCanSubmit] = useState(true);
  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  
  // Handle credit check callback from CreditEstimator
  const handleCreditCheck = useCallback((creditData) => {
    setCreditCheck(creditData);
    setCanSubmit(creditData?.hasCredits ?? true);
  }, []);
  // Website analysis state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isAnalyzingWebsite, setIsAnalyzingWebsite] = useState(false);
  // Store AI-generated column examples
  const [columnExamples, setColumnExamples] = useState({});

  // Set default model when component mounts
  useEffect(() => {
    // Set default values if not already set
    if (!filterParams || typeof filterParams !== 'object') {
      setFilterParams({
        model: 'gpt-4o',
        reasoningText: '',
        chunkSize: 300,
        logicMode: 'AND'
      });
    } else if (!filterParams.model) {
      setFilterParams(prev => ({
        ...prev,
        model: 'gpt-4o',
        logicMode: prev.logicMode || 'AND'
      }));
    }
  }, [setFilterParams]); // Only run on mount
  
  // Models available for filtering (matches backend configuration)
  const modelOptions = [
    // NEW! April 2025 Models (latest reasoning models)
    { value: 'o3', label: 'O3 - Flagship reasoning model with best-in-class performance' },
    { value: 'o4-mini', label: 'O4 Mini - Fast, powerful reasoning model with strong math & coding' },
    { value: 'gpt-4o', label: 'GPT-4o - Fast, intelligent, flexible flagship model', default: true },
    
    // Previous reasoning models (o-series)
    { value: 'o3-mini', label: 'O3 Mini - Fast, flexible reasoning model' },
    { value: 'o1', label: 'O1 - High-intelligence reasoning model' },
    { value: 'o1-mini', label: 'O1 Mini - Faster, affordable reasoning model' },
    { value: 'o1-pro', label: 'O1 Pro - Enhanced O1 with more compute' },
    
    // Flagship chat models
    { value: 'gpt-4.1', label: 'GPT-4.1 - Flagship for complex tasks' },
    { value: 'gpt-4o', label: 'GPT-4o - Fast, intelligent, flexible' },
    { value: 'chatgpt-4o-latest', label: 'ChatGPT-4o - Latest ChatGPT model' },
    
    // Cost-optimized models
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini - Balanced for cost/performance' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano - Fastest, most cost-effective' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini - Fast, affordable small model' }
  ];

  // Handle changes to the main filter parameters
  const handleParamChange = (param) => (event) => {
    const value = event.target.value;
    setFilterParams(prev => ({
      ...prev,
      [param]: value
    }));
    // Removed localStorage save on every keystroke - now handled by onBlur
  };

  // Temperature is no longer used - o3 models don't support it

  // Chunk size is now fixed at 300 - removing user control to simplify UI

  // Save filter config to localStorage (separated for performance)
  const saveFilterConfigToStorage = useCallback((config) => {
    try {
      const userId = localStorage.getItem('currentUserId') || 'anonymous';
      const storageKey = `filterToolState_${userId}`;
      const existingState = JSON.parse(localStorage.getItem(storageKey)) || {};
      
      localStorage.setItem(storageKey, JSON.stringify({
        ...existingState,
        filterConfig: config
      }));
      
      localStorage.setItem('filterColumnConfig', JSON.stringify(config));
    } catch (e) {
      console.error('Error saving filterConfig to localStorage:', e);
    }
  }, []);

  // Handle changes to column-specific configuration (no localStorage on every keystroke)
  const handleColumnConfigChange = (column, param) => (event) => {
    const updatedConfig = { ...filterConfig };
    
    if (!updatedConfig[column]) {
      updatedConfig[column] = {};
    }
    
    updatedConfig[column][param] = event.target.value;
    setFilterConfig(updatedConfig);
    // Removed localStorage saves and console.logs for performance
  };

  // Handle blur event to save to localStorage when user finishes typing
  const handleColumnConfigBlur = useCallback(() => {
    saveFilterConfigToStorage(filterConfig);
  }, [filterConfig, saveFilterConfigToStorage]);

  // Save filter config when component unmounts or user navigates away
  // Use a ref to avoid recreating cleanup function on every filterConfig change
  const filterConfigRef = useRef(filterConfig);
  filterConfigRef.current = filterConfig;
  
  useEffect(() => {
    return () => {
      // Cleanup function runs when component unmounts
      if (filterConfigRef.current && Object.keys(filterConfigRef.current).length > 0) {
        // Call saveFilterConfigToStorage directly to avoid dependency issues
        try {
          const userId = localStorage.getItem('currentUserId') || 'anonymous';
          const storageKey = `filterToolState_${userId}`;
          const existingState = JSON.parse(localStorage.getItem(storageKey)) || {};
          
          localStorage.setItem(storageKey, JSON.stringify({
            ...existingState,
            filterConfig: filterConfigRef.current
          }));
          
          localStorage.setItem('filterColumnConfig', JSON.stringify(filterConfigRef.current));
        } catch (e) {
          console.error('Error saving filterConfig to localStorage on unmount:', e);
        }
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Handle resetting all data
  const handleReset = () => {
    setResetDialogOpen(true);
  };

  // Confirm and execute reset
  const confirmReset = () => {
    // Reset all application state
    setFilterConfig({});
    setFilterParams(prev => ({
      ...prev,
      reasoningText: '',
      model: 'gpt-4o'
    }));
    setResetDialogOpen(false);
    
    // Navigate back to upload screen
    onBack();
    console.log('All data has been reset');
  };

  // Cancel reset
  const cancelReset = () => {
    setResetDialogOpen(false);
  };

  // Helper function to check if all criteria are filled
  const areAllCriteriaFilled = useCallback(() => {
    if (!selectedColumns || selectedColumns.length === 0) return false;
    
    return selectedColumns.every(column => {
      return filterConfig[column] && 
             filterConfig[column].instructions && 
             filterConfig[column].instructions.trim() !== '';
    });
  }, [selectedColumns, filterConfig]);

  // Generate smart placeholder text for column rules
  const getColumnPlaceholder = useCallback((columnName) => {
    // First, check if we have an AI-generated example
    if (columnExamples[columnName]) {
      return `e.g., ${columnExamples[columnName]}`;
    }
    
    // Fallback to strategic/tactical examples based on column name patterns
    const lowerColumnName = columnName.toLowerCase();
    
    if (lowerColumnName.includes('title') || lowerColumnName.includes('job') || lowerColumnName.includes('role')) {
      return 'e.g., target decision-makers in technology adoption, CTOs, Engineering VPs, IT Directors';
    }
    
    if (lowerColumnName.includes('size') || lowerColumnName.includes('employees') || lowerColumnName.includes('company')) {
      return 'e.g., focus on mid-market companies with growth potential, 100-1000 employees, Series B+ funded';
    }
    
    if (lowerColumnName.includes('industry') || lowerColumnName.includes('sector')) {
      return 'e.g., target sectors embracing digital transformation, SaaS, Healthcare, Fintech';
    }
    
    if (lowerColumnName.includes('location') || lowerColumnName.includes('country') || lowerColumnName.includes('region')) {
      return 'e.g., focus on tech-forward markets with strong economies, North America, Western Europe, APAC tech hubs';
    }
    
    if (lowerColumnName.includes('revenue') || lowerColumnName.includes('funding')) {
      return 'e.g., target financially stable companies ready to invest, $10M+ revenue, Series B+ funding';
    }
    
    if (lowerColumnName.includes('technology') || lowerColumnName.includes('tech') || lowerColumnName.includes('stack')) {
      return 'e.g., companies adopting modern infrastructure solutions, AWS, Kubernetes, microservices';
    }
    
    if (lowerColumnName.includes('department') || lowerColumnName.includes('team')) {
      return 'e.g., target teams driving digital initiatives, Engineering, Product, IT Operations';
    }
    
    // Generic strategic/tactical fallback
    return `e.g., companies/roles that would benefit from your solution, specific ${columnName.toLowerCase()} examples`;
  }, [columnExamples]);

  // Handle website analysis
  const handleAnalyzeWebsite = async () => {
    try {
      setError(null);
      setIsAnalyzingWebsite(true);

      // Basic URL validation
      let url = websiteUrl.trim();
      if (!url) {
        throw new Error('Please enter a website URL');
      }

      // Add https:// if no protocol specified
      if (!url.match(/^https?:\/\//i)) {
        url = 'https://' + url;
      }

      console.log('Analyzing website:', url);
      console.log('Selected columns for examples:', selectedColumns);

      // Call API to analyze website and extract product context + column examples
      const response = await apiPost('/api/analyze-website-context', {
        url: url,
        selectedColumns: selectedColumns || []
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze website');
      }

      const data = await response.json();
      console.log('Website analysis result:', data);

      // Update the reasoningText field with the extracted context
      if (data.context) {
        setFilterParams(prev => ({
          ...prev,
          reasoningText: data.context
        }));
        console.log('Updated context from website analysis');
      } else {
        throw new Error('No context could be extracted from the website');
      }

      // Store AI-generated column examples
      if (data.columnExamples && Object.keys(data.columnExamples).length > 0) {
        setColumnExamples(data.columnExamples);
        console.log('Received AI-generated column examples:', data.columnExamples);
      } else {
        console.log('No column examples generated by AI');
        setColumnExamples({});
      }

    } catch (error) {
      console.error('Website analysis error:', error);
      setError(`Website analysis failed: ${error.message}`);
    } finally {
      setIsAnalyzingWebsite(false);
    }
  };

  // Handle preview request - called when user clicks "Run" button
  const handlePreviewRequest = async () => {
    try {
      setPreviewError(null);
      setPreviewLoading(true);
      setPreviewModalOpen(true);
      setPreviewData(null);

      // Validate selected columns
      if (!selectedColumns || selectedColumns.length === 0) {
        throw new Error('Please select at least one column to filter');
      }

      // Check that all selected columns have criteria defined
      const missingCriteria = [];
      selectedColumns.forEach(column => {
        if (!filterConfig[column] || !filterConfig[column].instructions || !filterConfig[column].instructions.trim()) {
          missingCriteria.push(column);
        }
      });

      if (missingCriteria.length > 0) {
        throw new Error(`Please provide criteria for: ${missingCriteria.join(', ')}`);
      }

      // Check file data
      if (!fileData || !fileData.id) {
        throw new Error('File data is missing. Please upload your file again.');
      }

      // Build standardized column config
      const standardizedColumnConfig = {};
      selectedColumns.forEach(column => {
        standardizedColumnConfig[column] = filterConfig[column] || { instructions: '' };
      });

      console.log('[Preview] Requesting preview with config:', {
        fileId: fileData.id,
        selectedColumns,
        model: filterParams.model
      });

      // Call preview API
      const response = await apiPost('/api/filter/preview', {
        fileId: fileData.id,
        selectedColumns,
        reasoningText: filterParams.reasoningText,
        model: filterParams.model,
        filterConfig: { columnConfig: standardizedColumnConfig }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Preview failed with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[Preview] Preview results received:', data);

      setPreviewData(data);
      setPreviewLoading(false);

    } catch (error) {
      console.error('[Preview] Error:', error);
      setPreviewError(error.message || 'Failed to generate preview');
      setPreviewLoading(false);
    }
  };

  // Handle confirm from preview modal - proceed with actual job creation
  const handlePreviewConfirm = () => {
    setPreviewModalOpen(false);
    handleApplyFilter();
  };

  // Handle close preview modal
  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewData(null);
    setPreviewError(null);
  };

  // Start the filtering process
  const handleApplyFilter = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Debug: Log fileData details to check if it exists and has the expected id property
      console.log('DEBUG - fileData:', fileData);
      if (fileData) {
        console.log('DEBUG - fileData.id:', fileData.id);
      } else {
        console.error('DEBUG - fileData is undefined or null!');
      }
      
      if (!selectedColumns || selectedColumns.length === 0) {
        throw new Error('Please select at least one column to filter');
      }
      
      // Check that all selected columns have criteria defined
      const missingCriteria = [];
      selectedColumns.forEach(column => {
        if (!filterConfig[column] || !filterConfig[column].instructions || !filterConfig[column].instructions.trim()) {
          missingCriteria.push(column);
        }
      });
      
      if (missingCriteria.length > 0) {
        throw new Error(`Please provide criteria for the following columns: ${missingCriteria.join(', ')}`);
      }
      
      // Validate credit availability
      if (!canSubmit || (creditCheck && !creditCheck.hasCredits)) {
        throw new Error('Insufficient credits to start this filter job. Please reduce the dataset size or upgrade your plan.');
      }
      
      // Removed required validation for reasoningText - now it's optional
      
      // Store reasoning text in localStorage for persistence
      localStorage.setItem('filterReasoningText', filterParams.reasoningText);
      
      // Create a standardized filter configuration with columnConfig structure
      const standardizedColumnConfig = {};
      
      // Ensure every selected column has an entry in columnConfig
      selectedColumns.forEach(column => {
        // Keep any existing column configuration or create a new one
        standardizedColumnConfig[column] = filterConfig[column] || { instructions: '' };
        console.log(`Setting up column config for ${column}:`, standardizedColumnConfig[column]);
      });
      
      // Log the standardized configuration
      console.log('Standardized columnConfig:', standardizedColumnConfig);
      
      // CRITICAL FIX: Save the standardized column config to localStorage
      // This ensures column instructions are preserved between components
      // Use the same pattern as reasoningText which we know works
      localStorage.setItem('filterColumnConfig', JSON.stringify(standardizedColumnConfig));
      
      // DIAGNOSTIC LOG: Specifically verify column instructions are saved
      console.log('DIAGNOSTIC CHECK - Column instructions saved to localStorage:');
      selectedColumns.forEach(column => {
        if (standardizedColumnConfig[column] && standardizedColumnConfig[column].instructions) {
          console.log(`  - ${column}: "${standardizedColumnConfig[column].instructions}"`);
        } else {
          console.log(`  - ${column}: <no instructions>`);
        }
      });
      
      // Verify it can be retrieved properly
      try {
        const testRetrieve = JSON.parse(localStorage.getItem('filterColumnConfig'));
        console.log('DIAGNOSTIC: Retrieved from localStorage successfully:', testRetrieve);
      } catch(e) {
        console.error('DIAGNOSTIC: Error retrieving from localStorage', e);
      }
      
      // Always create a new job - templates only pre-fill forms, don't create jobs
      
      console.log('Applying filter with parameters:', {
        fileId: fileData?.id,
        selectedColumns,
        reasoningText: filterParams.reasoningText,
        model: filterParams.model,
        columnConfig: standardizedColumnConfig
      });
      
      // Check if fileData exists and has an id before making the API call
      if (!fileData || !fileData.id) {
        throw new Error('File data is missing or incomplete. Please upload your file again.');
      }
      
      // Validate job name only when creating a new job
      if (!jobName || jobName.trim() === '') {
        throw new Error('Job name is missing. Please go back to the upload page and provide a job name.');
      }
      
      const response = await apiPost('/api/filter', {
        jobName: jobName.trim(),
        fileId: fileData.id,
        selectedColumns,
        reasoningText: filterParams.reasoningText,
        model: filterParams.model,
        chunkSize: parseInt(filterParams.chunkSize, 10) || 300,
        logicMode: filterParams.logicMode || 'AND',
        filterConfig: { columnConfig: standardizedColumnConfig }, // Match backend's expected structure
        rowCount: fileData?.rowCount || fileData?.data?.length || 0 // Add row count for credit pre-authorization
      });
      
      if (!response.ok) {
        // Handle error responses
        try {
          const errorData = await response.json();
          // Check if error is an object or string and handle accordingly
          if (typeof errorData.error === 'object') {
            throw new Error(JSON.stringify(errorData.error) || `Filter failed with status: ${response.status}`);
          } else {
            throw new Error(errorData.error || `Filter failed with status: ${response.status}`);
          }
        } catch (jsonError) {
          // If JSON parsing fails, use the status text
          throw new Error(`Filter failed: ${response.statusText || response.status}`);
        }
      }
      
      // Parse the successful response
      const responseData = await response.json();
      
      // Use the new filter ID from the response
      const newFilterId = responseData.filterId;
      setFilterId(newFilterId);
      
      // Job created successfully! Redirect to Activity History immediately
      setLoading(false);
      if (onSuccess) {
        onSuccess();
        return; // Exit early - don't wait for processing
      }
      
      // Template overrides will be applied after job completion (simpler approach)
      
      setLoading(false);
      
      // Fallback: Move to results page (only if onSuccess wasn't called)
      onNext();
    } catch (error) {
      setLoading(false);
      console.error('Filter error:', error);
      
      // Ensure we're displaying a string, not an object
      let errorMessage = 'An error occurred while applying filters';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        // If error is an object, stringify it for display
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error occurred';
        }
      }
      
      setError(errorMessage);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Product/Service Context Section on left, Credit Estimator on right */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {/* Left side - Product/Service Context */}
        <Paper variant="outlined" sx={{ 
          flex: 1, 
          p: 3, 
          bgcolor: '#161c3e', 
          borderColor: 'rgba(255, 255, 255, 0.1)' 
        }}>
        <Typography variant="h6" gutterBottom>
          Product/Service Context (Optional)
        </Typography>
        
        {/* Website Analysis Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Give us your website and we will analyze it for you
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 1 }}>
            <TextField
              label="Website URL (Optional)"
              placeholder="https://yourcompany.com"
              fullWidth
              margin="normal"
              size="small"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
            <Button
              variant="contained"
              sx={{ 
                mb: 1, 
                minWidth: 120, 
                height: 40, // Reduced from 56 to 40 (about 70%)
              }}
              onClick={handleAnalyzeWebsite}
              disabled={!websiteUrl.trim() || isAnalyzingWebsite}
              startIcon={isAnalyzingWebsite ? <CircularProgress size={16} /> : null}
            >
              {isAnalyzingWebsite ? 'Analyzing...' : 'Analyze'}
            </Button>
          </Box>
        </Box>

        {/* Context Field */}
        <TextField
          label="Provide context on the product or service you are selling (Optional)"
          multiline
          rows={4}
          value={filterParams.reasoningText}
          onChange={handleParamChange('reasoningText')}
          onBlur={() => {
            // Save reasoning text to localStorage on blur
            if (filterParams.reasoningText) {
              localStorage.setItem('filterReasoningText', filterParams.reasoningText);
            }
          }}
          placeholder="Context about your product or service will be populated here from website analysis, or you can write your own..."
          fullWidth
          margin="normal"
          helperText="This context helps the AI understand what you're selling to make better qualification decisions"
        />
        </Paper>
        
        {/* Right side - Credit Estimator */}
        <Box sx={{ width: '45%' }}>
          <ListCleanerCreditEstimator 
            rowCount={fileData?.rowCount || fileData?.data?.length || 0}
            onCreditCheck={handleCreditCheck}
            showTierLimits={true}
            autoCheck={true}
          />
        </Box>
      </Box>
      
      {/* Qualifying Criteria Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        <Typography variant="h6" gutterBottom>
          Qualifying Criteria
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Provide instructions to the agent for each column that you have selected.
        </Typography>
        
        {/* AND/OR Selection with Natural Language on same line */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 'auto' }}>
            A qualified lead:
          </Typography>
          <FormControl sx={{ minWidth: 350 }}>
            <Select
              value={filterParams.logicMode || 'AND'}
              onChange={handleParamChange('logicMode')}
              variant="outlined"
              sx={{ 
                '& .MuiSelect-select': { 
                  display: 'flex', 
                  alignItems: 'center' 
                } 
              }}
            >
              <MenuItem value="AND">
                Matches all criteria below
              </MenuItem>
              <MenuItem value="OR">
                Matches at least one criteria below
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        {selectedColumns.length > 0 ? (
          <Box>
            {selectedColumns.map((column, index) => (
              <Box key={column}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mb: 1,
                  p: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.02)'
                }}>
                  <Typography sx={{ 
                    minWidth: 200,
                    maxWidth: 200,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {column}
                  </Typography>
                  <AutoCompleteField
                    fullWidth
                    size="small"
                    value={filterConfig[column]?.instructions || ''}
                    onChange={handleColumnConfigChange(column, 'instructions')}
                    onBlur={handleColumnConfigBlur}
                    variant="outlined"
                    recommendation={getColumnPlaceholder(column)}
                    required
                    error={!filterConfig[column]?.instructions?.trim()}
                  />
                  {filterConfig[column] && filterConfig[column].instructions && (
                    <Chip 
                      label="Criteria" 
                      size="small" 
                      color="primary" 
                    />
                  )}
                </Box>
                
                {/* Show AND/OR indicator between columns with reduced spacing */}
                {index < selectedColumns.length - 1 && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    py: 0.5,
                    my: 0.5
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#FFFFFF',
                        backgroundColor: filterParams.logicMode === 'OR' ? '#faa32c' : '#0095FF', // Wyspur orange for OR, blue for AND
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      {filterParams.logicMode || 'AND'}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center">
            No columns selected. Please go back and select columns to filter.
          </Typography>
        )}
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          onClick={onBack}
          variant="outlined" 
          sx={{ 
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'text.secondary',
            textTransform: 'none',
            borderRadius: '4px'  
          }}
        >
          Previous
        </Button>
        
        {/* New Reset All Data button */}
        <Button 
          variant="outlined" 
          color="secondary"
          onClick={handleReset}
          startIcon={<RefreshIcon />}
          sx={{ 
            textTransform: 'none',
            borderRadius: '4px'
          }}
        >
          Reset
        </Button>
        
        <Button
          variant="contained"
          onClick={handlePreviewRequest}
          disabled={selectedColumns.length === 0 || !areAllCriteriaFilled() || !canSubmit}
          sx={{ minWidth: 100 }}
        >
          Run
        </Button>
      </Box>
      
      {/* Save Filter Dialog */}
      <SaveFilterDialog 
        open={saveDialogOpen} 
        onClose={(success) => {
          setSaveDialogOpen(false);
          if (success) {
            // Show success message or notification
            setError(null); // Clear any errors
          }
        }} 
        filterConfig={{
          selectedColumns,
          reasoningText: filterParams.reasoningText,
          model: filterParams.model,
          temperature: filterParams.temperature || 0.7,
          chunkSize: parseInt(filterParams.chunkSize, 10) || 300,
          logicMode: filterParams.logicMode || 'AND',
          // Ensure we're using the standardized columnConfig structure with proper instructions
          columnConfig: Object.fromEntries(
            selectedColumns.map(column => {
              // Ensure we have proper instructions for this column
              const columnData = filterConfig[column] || {};
              return [
                column, 
                { instructions: columnData.instructions || '' }
              ];
            })
          )
        }}
      />
      
      {/* Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={cancelReset}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          Reset?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            This will clear all your filter configurations and selections. You'll need to start over from the beginning. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelReset} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmReset} color="error" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Modal */}
      <PreviewModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        onConfirm={handlePreviewConfirm}
        previewData={previewData}
        loading={previewLoading}
        error={previewError}
        creditCost={creditCheck?.creditsRequired || fileData?.rowCount || 0}
      />
    </Box>
  );
};

export default ConfigureFilters;
